import React, { useState, useEffect } from 'react'
import { Calendar, Trash2 } from 'lucide-react'

interface Servico {
  id: string
  nome: string
  valor_padrao: number
  servico_avulso?: boolean
}

interface ServicoAvulsoPagamento {
  numero_parcela: number
  data_pagamento: string
  valor: number
}

interface ServicoAvulsoConfig {
  servico_id: string
  numero_parcelas: number
  valor_total: number
  pagamentos: ServicoAvulsoPagamento[]
}

interface DescontoInfo {
  tipo: 'percentual' | 'fixo' | ''
  valor: number
}

interface ServicoAvulsoConfigProps {
  servico: Servico
  config?: ServicoAvulsoConfig
  desconto?: DescontoInfo
  onChange: (servicoId: string, config: ServicoAvulsoConfig) => void
  onRemove: (servicoId: string) => void
}

const ServicoAvulsoConfig: React.FC<ServicoAvulsoConfigProps> = ({
  servico,
  config,
  desconto,
  onChange,
  onRemove
}) => {
  const [numeroParcelas, setNumeroParcelas] = useState(config?.numero_parcelas || 1)
  const [valorTotal, setValorTotal] = useState(config?.valor_total || servico.valor_padrao)
  const [parcelasEditaveis, setParcelasEditaveis] = useState(false)
  const [pagamentos, setPagamentos] = useState<ServicoAvulsoPagamento[]>(
    config?.pagamentos || []
  )

  // Calcular valor final considerando desconto
  const calcularValorComDesconto = (valorOriginal: number, descontoInfo?: DescontoInfo) => {
    if (!descontoInfo || !descontoInfo.tipo || descontoInfo.valor === 0) {
      return valorOriginal
    }
    
    if (descontoInfo.tipo === 'percentual') {
      return valorOriginal * (1 - descontoInfo.valor / 100)
    } else if (descontoInfo.tipo === 'fixo') {
      return Math.max(0, valorOriginal - descontoInfo.valor)
    }
    
    return valorOriginal
  }

  const valorFinal = calcularValorComDesconto(valorTotal, desconto)

  // Validar soma das parcelas
  const validarParcelas = () => {
    const somaParcelas = pagamentos.reduce((sum, p) => sum + p.valor, 0)
    const diferenca = Math.abs(somaParcelas - valorFinal)
    return {
      soma_parcelas: somaParcelas,
      diferenca: diferenca,
      valido: diferenca < 0.01 // Tolerância de 1 centavo
    }
  }

  const validacao = validarParcelas()

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  // Atualizar pagamentos quando número de parcelas muda
  useEffect(() => {
    const valorPorParcela = valorFinal / numeroParcelas
    const novosPagamentos: ServicoAvulsoPagamento[] = []

    for (let i = 1; i <= numeroParcelas; i++) {
      const pagamentoExistente = pagamentos.find(p => p.numero_parcela === i)
      novosPagamentos.push({
        numero_parcela: i,
        data_pagamento: pagamentoExistente?.data_pagamento || '',
        valor: valorPorParcela
      })
    }

    // Só atualizar se não estiver em modo de edição manual
    if (!parcelasEditaveis) {
      setPagamentos(novosPagamentos)
    }
  }, [numeroParcelas, valorFinal, parcelasEditaveis])

  // Notificar mudanças para o componente pai
  useEffect(() => {
    const configAtualizada: ServicoAvulsoConfig = {
      servico_id: servico.id,
      numero_parcelas: numeroParcelas,
      valor_total: valorTotal,
      pagamentos: pagamentos
    }
    onChange(servico.id, configAtualizada)
  }, [servico.id, numeroParcelas, valorTotal, pagamentos, onChange])

  const handleDataChange = (numeroParcela: number, novaData: string) => {
    setPagamentos(prev => 
      prev.map(p => 
        p.numero_parcela === numeroParcela 
          ? { ...p, data_pagamento: novaData }
          : p
      )
    )
  }

  const handleValorParcelaChange = (numeroParcela: number, novoValor: string) => {
    const valor = parseFloat(novoValor) || 0
    setPagamentos(prev => 
      prev.map(p => 
        p.numero_parcela === numeroParcela 
          ? { ...p, valor: valor }
          : p
      )
    )
  }

  const handleValorTotalChange = (novoValor: string) => {
    const valor = parseFloat(novoValor) || 0
    setValorTotal(valor)
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-gray-900">{servico.nome}</h4>
          <p className="text-sm text-gray-500">Configuração de Serviço Avulso</p>
        </div>
        <button
          onClick={() => onRemove(servico.id)}
          className="text-red-600 hover:text-red-800 p-1"
          title="Remover configuração"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Informações de desconto e valor */}
      {desconto && desconto.valor > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Valor Original:</span>
              <span className="font-medium">{formatCurrency(valorTotal)}</span>
            </div>
            <div className="flex justify-between text-red-600">
              <span>Desconto ({desconto.tipo === 'percentual' ? `${desconto.valor}%` : 'Fixo'}):</span>
              <span>-{formatCurrency(desconto.tipo === 'percentual' ? valorTotal * desconto.valor / 100 : desconto.valor)}</span>
            </div>
            <hr className="border-blue-200" />
            <div className="flex justify-between font-bold text-blue-900">
              <span>Valor Final:</span>
              <span>{formatCurrency(valorFinal)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Configurações básicas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Valor Total do Serviço Avulso
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
              R$
            </span>
            <input
              type="number"
              value={valorTotal}
              onChange={(e) => handleValorTotalChange(e.target.value)}
              min="0"
              step="0.01"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="0,00"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Número de Parcelas
          </label>
          <select
            value={numeroParcelas}
            onChange={(e) => setNumeroParcelas(parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map(num => (
              <option key={num} value={num}>
                {num}x de {formatCurrency(valorFinal / num)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Opção de edição manual */}
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id={`editar-parcelas-${servico.id}`}
          checked={parcelasEditaveis}
          onChange={(e) => setParcelasEditaveis(e.target.checked)}
          className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500 focus:ring-2"
        />
        <label htmlFor={`editar-parcelas-${servico.id}`} className="text-sm font-medium text-gray-700">
          Editar valores das parcelas manualmente
        </label>
      </div>

      {/* Datas de pagamento */}
      <div>
        <h5 className="font-medium text-gray-900 mb-3">Datas de Pagamento</h5>
        <div className="space-y-3">
          {pagamentos.map((pagamento) => (
            <div key={pagamento.numero_parcela} className="flex items-center space-x-3">
              <div className="flex-shrink-0 w-16 text-sm text-gray-600">
                {pagamento.numero_parcela}ª parcela
              </div>
              <div className="flex-1">
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="date"
                    value={pagamento.data_pagamento}
                    onChange={(e) => handleDataChange(pagamento.numero_parcela, e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    required
                  />
                </div>
              </div>
              <div className="flex-shrink-0 w-32">
                {parcelasEditaveis ? (
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs">R$</span>
                    <input
                      type="number"
                      value={pagamento.valor}
                      onChange={(e) => handleValorParcelaChange(pagamento.numero_parcela, e.target.value)}
                      min="0"
                      step="0.01"
                      className="w-full pl-6 pr-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                ) : (
                  <span className="text-sm text-gray-900 font-medium">
                    {formatCurrency(pagamento.valor)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Validação e Resumo */}
      <div className={`rounded-lg p-4 ${
        validacao.valido ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
      }`}>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Valor Final (com desconto):</span>
            <span className="font-medium text-gray-900">{formatCurrency(valorFinal)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Soma das Parcelas:</span>
            <span className={`font-medium ${validacao.valido ? 'text-green-700' : 'text-red-700'}`}>
              {formatCurrency(validacao.soma_parcelas)}
            </span>
          </div>
          {!validacao.valido && (
            <div className="flex justify-between items-center text-red-600">
              <span>Diferença:</span>
              <span className="font-medium">
                {validacao.diferenca > 0 ? '+' : ''}{formatCurrency(validacao.soma_parcelas - valorFinal)}
              </span>
            </div>
          )}
          <div className="flex items-center space-x-2 mt-2">
            {validacao.valido ? (
              <>
                <span className="text-green-600">✅</span>
                <span className="text-green-700 font-medium">Valores corretos</span>
              </>
            ) : (
              <>
                <span className="text-red-600">⚠️</span>
                <span className="text-red-700 font-medium">Ajuste os valores das parcelas</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ServicoAvulsoConfig
